import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { environment } from 'environments/environment';
import { AuthService } from 'app/services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

declare const FB: any;

@Component({
  selector: 'app-coming-soon',
  templateUrl: './coming-soon.component.html',
  styleUrls: ['./coming-soon.component.scss'],
})
export class ComingSoonComponent implements OnInit, OnDestroy {
  registrationForm!: FormGroup;
  registered = false;
  accessToken: string = '';
  private subscription: Subscription = new Subscription();

  countdownDays: number = 0;
  countdownHours: number = 0;
  countdownMinutes: number = 0;
  countdownSeconds: number = 0;
  websiteUrl = 'https://cleverdeals.net';

  isLoading = false;
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.startCountdown();
    this.initializeForm();
    this.handleGoogleAuthCallback();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.loadFacebookSDK();
  }

  private startCountdown(): void {
    // Set to 10 AM UK time on Monday the 7th April 2025
    const launchDate = new Date('2025-04-07T10:00:00+01:00');

    // Update the countdown every second
    this.subscription = interval(1000).subscribe(() => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance > 0) {
        this.countdownDays = Math.floor(distance / (1000 * 60 * 60 * 24));
        this.countdownHours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        this.countdownMinutes = Math.floor(
          (distance % (1000 * 60 * 60)) / (1000 * 60)
        );
        this.countdownSeconds = Math.floor((distance % (1000 * 60)) / 1000);
      } else {
        // Countdown finished
        this.countdownDays = 0;
        this.countdownHours = 0;
        this.countdownMinutes = 0;
        this.countdownSeconds = 0;
        this.subscription.unsubscribe();
      }
    });
  }

  // Initialize registration form with validators
  private initializeForm() {
    this.registrationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.emailValidator]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
    });
  }

  // Custom validator for email
  private emailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value) ? null : { invalidEmail: true };
  }

  // Form Submission Method
  onRegister() {
    // Mark all fields as touched to trigger validation display
    this.markFormGroupTouched(this.registrationForm);

    // Check form validity
    if (this.registrationForm.valid) {
      this.isLoading = true;
      const formData = this.registrationForm.value;

      // Prepare data for backend
      const registrationPayload = {
        CustomerName:
          formData.firstName.trim() + ' ' + formData.lastName.trim(),
        Email: formData.email.toLowerCase(),
        Password: formData.email.toLowerCase(),
      };

      this.authService.signUp(registrationPayload).subscribe(
        (response) => {
          if (response.success) {
            this.registered = true;
            this.isLoading = false;
          } else {
            this.toastr.error(response.message);
            this.isLoading = false;
            console.error('Registration failed:', response.message);
          }
        },
        (error) => {
          this.isLoading = false;
          console.error('Registration error:', error);
        }
      );
    }
  }

  // Utility method to mark all controls as touched
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Error message getters for form validation
  getErrorMessage(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors) return '';

    if (control.errors['required']) {
      return `${fieldName} is required`;
    }
    if (control.errors['email']) {
      return 'Invalid email format';
    }
    if (control.errors['minlength']) {
      return `${fieldName} must be at least ${control.errors['minlength'].requiredLength} characters`;
    }
    if (control.errors['invalidEmail']) {
      return 'Invalid email address';
    }
    return '';
  }

  // Social Registration Methods
  signUpWithGoogle() {
    const clientId = environment.googleClientId;
    const redirectUri = environment.googleRedirectUri;
    const scope = 'openid email profile';
    const responseType = 'code';
    const state = 'prelaunch'; // Add state parameter to identify prelaunch registration

    window.location.href = `https://accounts.google.com/o/oauth2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&access_type=offline&prompt=consent&state=${state}`;
  }

  clearRouteQueryParams() {
    this.route.queryParams.subscribe((params) => {
      const keys = Object.keys(params);
      if (keys.length > 0) {
        const url = this.route.snapshot.url.join('/');
        this.route.snapshot.queryParams = {};
        window.history.replaceState({}, '', url);
      }
    });
  }

  handleGoogleAuthCallback() {
    this.route.queryParams.subscribe((params) => {
      const authCode = params['code'];
      if (authCode) {
        this.authService
          .socialLogin({ Provider: 'google', code: authCode })
          .subscribe(
            (response) => {
              if (response.success) {
                this.accessToken = response.data?.jwToken?.token;
                this.clearRouteQueryParams();
                this.registered = true;
              }
            },
            (error) => {
              console.error('Social login error:', error);
              this.clearRouteQueryParams();
            }
          );
      }
    });
  }

  loadFacebookSDK() {
    if (!document.getElementById('facebook-jssdk')) {
      let script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        (window as any).FB.init({
          appId: environment.facebookClientId,
          cookie: true,
          xfbml: true,
          version: 'v18.0', // Use latest version
        });

        (window as any).FB.AppEvents.logPageView();
      };

      document.body.appendChild(script);
    }
  }

  signInWithFacebook() {
    FB.login(
      (res: any) => {
        if (res.authResponse) {
          const accessToken = res.authResponse.accessToken;
          this.fetchFacebookUserData(accessToken);
        }
      },
      { scope: environment.facebookScope }
    );
  }

  fetchFacebookUserData(accessToken: string) {
    FB.api(
      environment.facebookApiUrl,
      { fields: environment.facebookApiFields },
      (userInfo: any) => {
        this.authService
          .socialLogin({
            Provider: 'facebook',
            code: accessToken,
            CustomerName: userInfo.name,
            Email: userInfo.email,
          })
          .subscribe(
            (response) => {
              if (response.success) {
                this.registered = true;
              } else {
                console.error('Social registration failed:', response.message);
              }
            },
            (error) => {
              console.error('Social registration error:', error);
            }
          );
      }
    );
  }

  shareOnFacebook() {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.websiteUrl
      )}`,
      '_blank'
    );
  }

  shareOnTwitter() {
    const text = "Check out this amazing deals site that's coming soon!";
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        this.websiteUrl
      )}&text=${encodeURIComponent(text)}`,
      '_blank'
    );
  }

  shareOnLinkedIn() {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.websiteUrl
      )}`,
      '_blank'
    );
  }
}
